// The floating push-to-talk button for staff: hold to talk, release to listen. Everyone on the
// site's radio hears it live; nothing is recorded.
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Icon } from "@vavr/ui";
import { Radio, type RadioEngine, type RadioOptions, type RadioState } from "./radio.ts";
import type { RadioEngineFactory } from "../platform.ts";
import "./radio.css";

export interface RadioButtonProps {
  /** One-use ticket for the site's radio socket; with `pass`, also a pass for a background engine. */
  getTicket: RadioOptions["getTicket"];
  /** wss://…/api/v1/sites/<site>/radio (radioSocket). */
  wsBase: string;
  /** Changing it (another site) reconnects. */
  channelKey: string;
  /** Where it floats: above a bottom tab bar in the apps, the corner in the console. */
  placement?: "corner" | "above-tabs" | "kiosk";
  /** Keeps the channel connected while hidden (the kiosk during a handover). */
  hidden?: boolean;
  /** A native engine (platform.radioEngine); the web engine otherwise. */
  engine?: RadioEngineFactory;
  /** Turns on by itself, without the Radio button (the door tablet, switched on in its staff menu). */
  autoStart?: boolean;
  /** Plays the channel out loud with no talk button: the parent-facing door tablet. */
  listenOnly?: boolean;
}

/** wss URL of a site's radio socket, without a query; apiBase is "" on the web (same origin). */
export const radioSocket = (apiBase: string, siteId: string) =>
  `${(apiBase || (typeof location === "undefined" ? "" : location.origin)).replace(/^http/, "ws")}/api/v1/sites/${encodeURIComponent(siteId)}/radio`;

const LABEL: Record<RadioState["kind"], string> = {
  off: "Radio off", connecting: "Connecting", ready: "Hold to talk", listening: "", requesting: "Wait…",
  talking: "On air", busy: "", error: "",
};

export function RadioButton({ getTicket, wsBase, channelKey, placement = "corner", hidden = false, engine, autoStart = false, listenOnly = false }: RadioButtonProps) {
  const [state, setState] = useState<RadioState>({ kind: "off" });
  const [speaker, setSpeaker] = useState(true);
  const radio = useRef<RadioEngine | null>(null);
  const ticket = useRef(getTicket);
  ticket.current = getTicket;

  // A native engine attaches to a radio that may already be on (started before the app was
  // closed). Leaving the screen (sign-out, the shift ends, another site) turns the radio off.
  useEffect(() => {
    const o: RadioOptions = { getTicket: (pass) => ticket.current(pass), wsBase, onState: setState };
    const r = engine ? engine(o) : new Radio(o);
    radio.current = r;
    if (autoStart && r.current.kind === "off") void r.start();
    return () => { r.stop(); r.dispose(); radio.current = null; };
  }, [channelKey, wsBase, engine, autoStart]);

  const on = state.kind !== "off";
  const background = radio.current?.background ?? false;

  // The web engine never leaves the microphone open behind a backgrounded tab.
  useEffect(() => {
    if (engine) return;
    const hide = () => { if (document.visibilityState === "hidden") radio.current?.releaseButton(); };
    document.addEventListener("visibilitychange", hide);
    return () => document.removeEventListener("visibilitychange", hide);
  }, [engine]);
  const down = (e: PointerEvent<HTMLButtonElement>) => {
    if (!on) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    radio.current?.press();
  };
  const up = () => radio.current?.releaseButton();
  const key = (e: KeyboardEvent<HTMLButtonElement>, pressed: boolean) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    if (e.repeat) return;
    pressed ? radio.current?.press() : radio.current?.releaseButton();
  };

  const status = state.kind === "listening" ? `${state.name} is talking`
    : state.kind === "busy" ? `${state.name} is talking. Wait, then try again.`
    : state.kind === "error" ? state.message
    : state.kind === "ready" && listenOnly ? "" // the door tablet only speaks up when someone talks
    : state.kind === "ready" && background ? "Hold to talk, or hold Volume Up" : LABEL[state.kind];

  return (
    <div className={`pmr pmr--${placement}`} data-state={state.kind} hidden={hidden}>
      {on && (status || state.kind === "talking") && <p className="pmr__status" role="status" aria-live="polite">{status}</p>}
      <div className="pmr__controls">
        {on && (
          <button type="button" className="pmr__small" aria-pressed={!speaker} aria-label={speaker ? "Mute the radio" : "Unmute the radio"}
            onClick={() => { const next = !speaker; setSpeaker(next); if (radio.current) radio.current.speaker = next; }}>
            <Icon name={speaker ? "speaker" : "speaker-off"} />
          </button>
        )}
        {listenOnly ? null : on ? (
          <button
            type="button" className="pmr__talk" aria-label={state.kind === "talking" ? "On air. Release to stop talking." : "Hold to talk on the staff radio"}
            onPointerDown={down} onPointerUp={up} onPointerCancel={up} onLostPointerCapture={up}
            onKeyDown={(e) => key(e, true)} onKeyUp={(e) => key(e, false)} onContextMenu={(e) => e.preventDefault()}
            disabled={state.kind === "connecting" || state.kind === "listening"}
          >
            <Icon name="mic" />
          </button>
        ) : (
          <button type="button" className="pmr__join" onClick={() => void radio.current?.start()} aria-label="Turn on the staff radio">
            <Icon name="mic" /><span>Radio</span>
          </button>
        )}
        {on && !listenOnly && <button type="button" className="pmr__small" aria-label="Turn off the radio" onClick={() => radio.current?.stop()}><Icon name="close" /></button>}
      </div>
    </div>
  );
}
